// Eine Aktivitäten-Liste (A-03 bis A-05, T47): Anrufe, E-Mails, SMS, WhatsApp, Meetings, Notizen oder die Custom Activities eines
// Typs (Felder als Spalten). Filter nach Mitarbeiter, Zeitraum, Richtung, Ergebnis und Lead stehen in der Adresse (teilbar),
// Sortierung und Spalten werden je Nutzer gemerkt. Ein Klick öffnet den Lead. Die Bedienleiste kommt von Agent 5B.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { aktivitaetenApi, type AktivitaetenArt, type AktivitaetenSortierung, type AktivitaetZeile, type Richtung } from "../api/aktivitaeten";
import { useEchtzeit } from "../api/echtzeit";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { Bedienleiste } from "../components/smartview/Bedienleiste";
import { csvSchreiben, dateiSpeichern } from "../components/smartview/csv";
import type { ListenSortierung } from "../components/smartview/listenVertrag";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { spaltenWaehlen, sortAusParam, sortFuerRoute, sortUmschalten, sortZuParam } from "../kontakte/liste/ansicht";
import { useGemerkteAnsicht } from "../kontakte/liste/gemerkteAnsicht";
import { AUSWAHL_SPALTE, ListenTabelle, type TabellenSpalte } from "../kontakte/liste/ListenTabelle";
import { useSpaltenLayout } from "../components/spaltenLayout";
import { useListe } from "../kontakte/liste/useListe";
import { T } from "../texte";
import { feldText } from "../werkzeuge/felder";
import { kopieren, useTasten } from "../werkzeuge/tasten";
import { AktivitaetenFilter, type FilterStand } from "./AktivitaetenFilter";
import { csvZeilen } from "./aktivitaetenExport";
import { AKTIVITAET_SORT_FELD, AKTIVITAET_STANDARD_SORT, spaltenFuer, spaltenKlasse } from "./aktivitaetenSpalten";
import { ARTEN, ansichtSchluessel, artInfo, artPfad, CUSTOM_EINHEIT, customPfad } from "./arten";
import { aktivitaetZelle } from "./Zellen";
import { istZeitraum, heuteTag, zeitraumGrenzen } from "./zeiten";
import "../kontakte/liste/liste.css";
import "./aktivitaeten.css";

interface Props {
  art: AktivitaetenArt;
  /** nur bei art = "custom": Typ der Custom Activity */
  typId?: string;
}

const SORT_SCHLUESSEL = Object.keys(AKTIVITAET_SORT_FELD);
const AUSWAHL_DEF: TabellenSpalte = { key: AUSWAHL_SPALTE, label: "", kopfTitel: "Auswahl", klasse: "liste-auswahlspalte" };
/** Close-Typen der Aktivität je Art, damit nur passende Echtzeit-Ereignisse den Hinweis „Neue Einträge“ auslösen */
const TYPEN: Record<AktivitaetenArt, string[]> = {
  anrufe: ["Call"],
  emails: ["Email", "EmailThread"],
  sms: ["SMS"],
  whatsapp: ["WhatsAppMessage"],
  meetings: ["Meeting"],
  notizen: ["Note"],
  custom: ["CustomActivity"],
};

const ersteRichtungAb = (key: string) => key === "activity_at" || key === "date_updated";
const zahlWort = (n: number, eins: string, viele: string) => `${n.toLocaleString("de-DE")} ${n === 1 ? eins : viele}`;

export function AktivitaetenListe({ art, typId }: Props) {
  const meta = useMeta();
  const navigate = useNavigate();
  const meldung = useMeldung();
  const [params, setParams] = useSearchParams();
  const info = artInfo(art);
  const typ = art === "custom" ? meta.typ(typId) : undefined;
  const titel = art === "custom" ? typ?.name ?? "Custom Activity" : info?.label ?? "Aktivitäten";
  const einheit = art === "custom" ? CUSTOM_EINHEIT : info?.einheit ?? { eins: "Eintrag", viele: "Einträge" };
  useSeitenKopf([{ label: T.nav.aktivitaeten, to: "/aktivitaeten" }, { label: titel }]);

  // Spalten dieser Art (bei Custom Activities mit den Feldern des Typs)
  const feldKoepfe = useMemo(() => (typ?.fields ?? []).map((f) => ({ id: f.id, name: f.name })), [typ]);
  const satz = useMemo(() => spaltenFuer(art, feldKoepfe), [art, feldKoepfe]);

  // Filter aus der Adresse
  const stand: FilterStand = useMemo(() => {
    const z = params.get("zeit");
    const r = params.get("richtung");
    return {
      user: params.get("user") || null,
      zeitraum: istZeitraum(z) ? z : "alle",
      eigenVon: params.get("von"),
      eigenBis: params.get("bis"),
      richtung: r === "ausgehend" || r === "eingehend" ? (r as Richtung) : null,
      ergebnisse: (params.get("ergebnis") ?? "").split(",").filter(Boolean),
    };
  }, [params]);
  const q = params.get("q") ?? "";
  const leadId = params.get("lead") ?? "";
  const sortParam = params.get("sort");
  const [eingabe, setEingabe] = useState(q);
  const [fokus, setFokus] = useState(0);
  const [neu, setNeu] = useState(false);

  // Gemerkte Ansicht je Nutzer
  const gemerkt = useGemerkteAnsicht(ansichtSchluessel(art, typId));
  const [spaltenWahl, setSpaltenWahl] = useState<string[] | null>(null);
  const [sortWahl, setSortWahl] = useState<ListenSortierung | "standard" | null>(null);
  const gezeigt = useMemo(() => spaltenWaehlen(spaltenWahl ?? gemerkt.ansicht?.spalten, satz.alle, satz.standard), [spaltenWahl, gemerkt.ansicht?.spalten, satz]);
  const layout = useSpaltenLayout(gemerkt.zugriff, gezeigt, (keys) => {
    setSpaltenWahl(keys);
    gemerkt.merken({ spalten: keys });
  });
  const gespeicherteSort = gemerkt.ansicht?.sortierung?.[0];
  const sort: ListenSortierung = useMemo(() => {
    const ausAdresse = sortAusParam(sortParam, SORT_SCHLUESSEL, { key: "", ab: false });
    if (ausAdresse.key) return ausAdresse;
    if (sortWahl === "standard") return AKTIVITAET_STANDARD_SORT;
    if (sortWahl) return sortWahl;
    if (gespeicherteSort && SORT_SCHLUESSEL.includes(gespeicherteSort.key)) return { key: gespeicherteSort.key, ab: gespeicherteSort.ab };
    return AKTIVITAET_STANDARD_SORT;
  }, [sortParam, sortWahl, gespeicherteSort]);

  const setParam = useCallback(
    (aenderungen: Record<string, string | null>) => {
      setParams(
        (alt) => {
          const neuP = new URLSearchParams(alt);
          for (const [k, v] of Object.entries(aenderungen)) {
            if (v) neuP.set(k, v);
            else neuP.delete(k);
          }
          return neuP;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (eingabe.trim() !== q) setParam({ q: eingabe.trim() || null });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingabe]);
  useEffect(() => {
    setEingabe((alt) => (alt.trim() === q ? alt : q));
  }, [q]);

  const grenzen = zeitraumGrenzen(stand.zeitraum, heuteTag(), stand.eigenVon, stand.eigenBis);
  const sortRoute = sortFuerRoute(sort, AKTIVITAET_SORT_FELD) as AktivitaetenSortierung | undefined;
  const schluessel = JSON.stringify([art, typId, q, leadId, stand, grenzen, sortRoute]);
  const holen = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      aktivitaetenApi.liste(
        {
          limit: 100,
          cursor,
          art,
          typ_id: art === "custom" ? typId : undefined,
          user_id: stand.user ?? undefined,
          lead_id: leadId || undefined,
          von: grenzen.von,
          bis: grenzen.bis,
          q: q || undefined,
          richtung: stand.richtung ?? undefined,
          ergebnis: art === "anrufe" ? stand.ergebnisse : undefined,
          sort: sortRoute,
        },
        signal,
      ),
    // `schluessel` fasst alle Eingaben zusammen
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schluessel],
  );
  const liste = useListe<AktivitaetZeile>(holen, schluessel);
  const { zeilen } = liste;

  useEffect(() => {
    setFokus(0);
    setNeu(false);
  }, [schluessel]);

  // Neue Einträge melden, nicht einfach nachladen (die Liste soll beim Lesen nicht springen)
  useEchtzeit(["activity.created", "activity.updated", "activity.deleted", "call.ended"], (e) => {
    if (e.type === "call.ended") {
      if (art === "anrufe") setNeu(true);
      return;
    }
    const typName = String((e.data as { activity_type?: string }).activity_type ?? "");
    if (!typName || TYPEN[art].includes(typName)) setNeu(true);
  });

  const oeffneLead = useCallback(
    (id: string, kontaktId?: string | null, aktivitaetId?: string | null) => {
      const q = [kontaktId ? `kontakt=${encodeURIComponent(kontaktId)}` : "", aktivitaetId ? `aktivitaet=${encodeURIComponent(aktivitaetId)}` : ""].filter(Boolean).join("&");
      navigate(`/lead/${encodeURIComponent(id)}${q ? `?${q}` : ""}`);
    },
    [navigate],
  );
  const zellenKontext = useMemo(() => ({ meta, oeffneLead }), [meta, oeffneLead]);

  const spalten: TabellenSpalte[] = useMemo(
    () => [
      AUSWAHL_DEF,
      ...gezeigt.flatMap((key) => {
        const def = satz.alle.find((s) => s.key === key);
        return def ? [{ key, label: def.label, sortierbar: !!def.sortierbar && key in AKTIVITAET_SORT_FELD, klasse: spaltenKlasse(key) } as TabellenSpalte] : [];
      }),
    ],
    [gezeigt, satz],
  );

  const sortKlick = (key: string) => {
    if (!(key in AKTIVITAET_SORT_FELD)) return;
    const neuS = sortUmschalten(sort, key, ersteRichtungAb(key));
    setSortWahl(neuS);
    setParam({ sort: sortZuParam(neuS, AKTIVITAET_STANDARD_SORT) });
    gemerkt.merken({ sortierung: [neuS] });
  };

  const aendern = (teil: Partial<FilterStand>) => {
    const p: Record<string, string | null> = {};
    if ("user" in teil) p.user = teil.user ?? null;
    if ("zeitraum" in teil) p.zeit = teil.zeitraum && teil.zeitraum !== "alle" ? teil.zeitraum : null;
    if ("eigenVon" in teil) p.von = teil.eigenVon ?? null;
    if ("eigenBis" in teil) p.bis = teil.eigenBis ?? null;
    if ("richtung" in teil) p.richtung = teil.richtung ?? null;
    if ("ergebnisse" in teil) p.ergebnis = teil.ergebnisse?.length ? teil.ergebnisse.join(",") : null;
    setParam(p);
  };
  const filterAktiv = !!(q || leadId || stand.user || stand.zeitraum !== "alle" || stand.richtung || stand.ergebnisse.length);
  const leadName = leadId ? zeilen[0]?.lead_name ?? "Lead" : null;

  // Menü „…“: Link, Neu laden, CSV
  const mehrPunkte: MenuePunkt[] = [
    { label: "Link kopieren", icon: "link", onClick: () => void kopieren(window.location.href).then((ok) => meldung.zeige(ok ? "ok" : "fehler", ok ? "Link kopiert." : "Kopieren hat nicht geklappt.")) },
    { label: "Neu laden", icon: "neu-laden", onClick: () => (setNeu(false), liste.neuLaden(true)) },
    {
      label: "Als CSV speichern",
      icon: "kopieren",
      trenner: true,
      deaktiviert: !zeilen.length,
      hinweis: `Die ${zahlWort(zeilen.length, "geladene Zeile", "geladenen Zeilen")} als Datei`,
      onClick: () => {
        const feldWert = (id: string, wert: unknown) => feldText(meta.feld(id), wert as never, { nutzerName: (u) => meta.nutzerName(u), kontaktName: () => "Kontakt" });
        dateiSpeichern(`${art === "custom" ? "custom-activities" : art}-${new Date().toISOString().slice(0, 10)}.csv`, csvSchreiben(csvZeilen(art, zeilen, feldKoepfe, feldWert)));
        meldung.ok(`${zahlWort(zeilen.length, "Zeile", "Zeilen")} gespeichert${liste.hatMehr ? " (nur die geladenen)" : ""}.`);
      },
    },
  ];

  // Wechsel zwischen den Arten über den Titel („Anrufe ▾“ wie in Close)
  const wechselPunkte: MenuePunkt[] = [
    ...ARTEN.map((a) => ({ label: a.label, icon: a.icon, rechts: art === a.art ? <Icon name="haken" groesse={14} /> : undefined, onClick: () => navigate(artPfad(a.art)) }) as MenuePunkt),
    ...(meta.meta?.custom_activity_types.length
      ? [
          { label: "Custom Activities", ueberschrift: true, trenner: true } as MenuePunkt,
          ...meta.meta.custom_activity_types
            .filter((t) => !t.is_archived || t.id === typId)
            .map((t) => ({ label: t.name, icon: "aktivitaet", rechts: art === "custom" && typId === t.id ? <Icon name="haken" groesse={14} /> : undefined, onClick: () => navigate(customPfad(t.id)) }) as MenuePunkt),
        ]
      : []),
    { label: "Übersicht", icon: "ansicht", trenner: true, onClick: () => navigate("/aktivitaeten") },
  ];

  const nameVonId = useMemo(() => new Map(zeilen.map((z) => [z.id, z.titel ?? z.lead_name ?? "Eintrag"])), [zeilen]);
  const [markiert, setMarkiert] = useState<Set<string>>(() => new Set());
  useEffect(() => setMarkiert(new Set()), [schluessel]);
  // Esc hebt die Markierung auf
  useTasten({ Escape: () => setMarkiert(new Set()) }, markiert.size > 0);

  return (
    <div className="liste-seite">
      <div className="liste-kopf">
        <h1 className="liste-kopf__titel akt-titel">
          <MenueKnopf knopfKlasse="akt-titelknopf" titel="Art der Aktivität wechseln" punkte={wechselPunkte} breite={260}>
            <span>{titel}</span>
            <Icon name="chevron-unten" groesse={13} />
          </MenueKnopf>
        </h1>
        <div className="liste-kopf__rechts">
          <MenueKnopf knopfKlasse="symbolknopf symbolknopf--rand" titel="Mehr" punkte={mehrPunkte} ausrichtung="end" breite={300}>
            <Icon name="mehr" groesse={16} />
          </MenueKnopf>
        </div>
      </div>
      <Bedienleiste
        liste={ansichtSchluessel(art, typId)}
        einheit={einheit}
        trefferZahl={liste.total}
        geladen={zeilen.length}
        laedt={liste.laedt}
        spalten={satz.alle}
        gezeigt={gezeigt}
        onSpalten={(keys) => {
          setSpaltenWahl(keys ?? spaltenWaehlen(null, satz.alle, satz.standard));
          gemerkt.merken({ spalten: keys });
        }}
        sort={sort.key === AKTIVITAET_STANDARD_SORT.key && sort.ab === AKTIVITAET_STANDARD_SORT.ab ? null : sort}
        sortStandard={[AKTIVITAET_STANDARD_SORT]}
        sortStandardName="Neueste zuerst"
        sortFelder={satz.alle.filter((s) => s.sortierbar && s.key in AKTIVITAET_SORT_FELD)}
        onSort={(s) => {
          setSortWahl(s ?? "standard");
          setParam({ sort: s ? sortZuParam(s, AKTIVITAET_STANDARD_SORT) : null });
          gemerkt.merken({ sortierung: s ? [s] : null });
        }}
        suche={{ wert: eingabe, onAendern: setEingabe, platzhalter: "In dieser Liste suchen …" }}
        auswahl={{ anzahl: markiert.size, onAlleMarkieren: zeilen.length ? () => setMarkiert(new Set(zeilen.map((z) => z.id))) : undefined, onAufheben: () => setMarkiert(new Set()) }}
        spaltenStandard={satz.standard}
        hinweis={
          <>
        <AktivitaetenFilter
          art={art}
          stand={stand}
          nutzer={meta.nutzerListe}
          leadName={leadName}
          onAendern={aendern}
          onLeadEntfernen={() => setParam({ lead: null })}
          onLeeren={() => {
            setEingabe("");
            setParam({ q: null, lead: null, user: null, zeit: null, von: null, bis: null, richtung: null, ergebnis: null });
          }}
          aktiv={filterAktiv}
        />
        {neu ? (
          <button type="button" className="knopf knopf--klein akt-neu" onClick={() => (setNeu(false), liste.neuLaden(true))}>
            <Icon name="neu-laden" groesse={13} />
            Neue Einträge anzeigen
          </button>
        ) : null}
          </>
        }
      />
      {liste.fehler ? <FehlerBox fehler={liste.fehler} onNochmal={() => liste.neuLaden()} /> : null}
      {liste.laedt && !zeilen.length ? (
        <Laden />
      ) : !liste.fehler && !zeilen.length ? (
        <Leer
          titel={`Keine ${einheit.viele} gefunden.`}
          text={filterAktiv ? "Suche oder Filter anpassen." : "Hier steht noch nichts. Neue Einträge erscheinen, sobald der Abgleich mit Close oder das CRM sie anlegt."}
        />
      ) : zeilen.length ? (
        <ListenTabelle<AktivitaetZeile>
          klasse={`tabelle--aktivitaeten tabelle--art-${art}`}
          breiten={layout.breiten}
          onBreite={layout.onBreite}
          onVerschieben={layout.onVerschieben}
          spalten={spalten}
          zeilen={zeilen}
          zeilenId={(z) => z.id}
          zelle={(z, s) => aktivitaetZelle(z, s.key, zellenKontext)}
          fokus={Math.min(fokus, zeilen.length - 1)}
          setFokus={setFokus}
          onOeffnen={(z) => z.lead_id && oeffneLead(z.lead_id, z.contact_id, z.id)}
          sort={sort}
          onSort={sortKlick}
          onEnde={liste.hatMehr ? liste.mehr : undefined}
          auswahl={{ markiert, onAendern: setMarkiert, beschriftung: (id) => `${nameVonId.get(id) ?? "Eintrag"} markieren`, alleTitel: `Alle geladenen ${einheit.viele} markieren` }}
          zeilenKlasse={(z) => (z.lead_id ? undefined : "tabelle__zeile--ohne-ziel")}
          fuss={
            liste.hatMehr ? (
              <div className="tabelle__mehr">
                <button type="button" className="knopf" disabled={liste.laedtMehr} onClick={liste.mehr}>
                  {liste.laedtMehr ? "Lädt …" : "Weitere laden"}
                </button>
              </div>
            ) : null
          }
        />
      ) : null}
    </div>
  );
}
